import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  keyframes,
  useTheme,
} from '@mui/material';
import {
  Campaign as InstantUpdateIcon,
  Folder as AssetIcon,
  Analytics as InfralytiqsIcon,
  Hub as AnamnesisIcon,
  People as PeopleIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../../state/store';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';
import { InstantUpdateDashboard } from '../../modules/InstantUpdate/Dashboard';
import { InfralytiqsDashboard } from '../../modules/Infralytiqs/Dashboard';
import { useModulePermissions } from '../../hooks/useModulePermissions';

interface ModuleOverviewItem {
  module: string;
  displayName: string;
  licenseType?: string;
  status: string;
  endsAt?: string | null;
  maxUsers: number;
  registeredUsers: number;
  remainingSeats: number;
  details: Record<string, number | null>;
}

type DashboardDialog = 'instantupdate.ai' | 'infralytiqs.com' | null;

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const glowPulse = keyframes`
  0%, 100% {
    box-shadow:
      0 0 0 1px var(--glow-border),
      0 0 12px 0 var(--glow-soft),
      0 4px 18px rgba(0, 0, 0, 0.12);
  }
  50% {
    box-shadow:
      0 0 0 1px var(--glow-border),
      0 0 22px 2px var(--glow-strong),
      0 6px 22px rgba(0, 0, 0, 0.16);
  }
`;

const MODULE_DISPLAY_NAMES: Record<string, string> = {
  'instantupdate.ai': 'Instant Update',
  'assetsmanagement.ai': 'Asset Management',
  'infralytiqs.com': 'Infralytiqs',
  'anamnesis.cloud': 'Anamnesis',
};

const emptyModuleOverview = (moduleId: string): ModuleOverviewItem => ({
  module: moduleId,
  displayName: MODULE_DISPLAY_NAMES[moduleId] || moduleId,
  status: 'active',
  maxUsers: 0,
  registeredUsers: 0,
  remainingSeats: 0,
  details: {},
});

const MODULE_ICONS: Record<string, React.ReactElement> = {
  'instantupdate.ai': <InstantUpdateIcon />,
  'assetsmanagement.ai': <AssetIcon />,
  'infralytiqs.com': <InfralytiqsIcon />,
  'anamnesis.cloud': <AnamnesisIcon />,
};

const MODULE_ACCENTS: Record<string, string> = {
  'instantupdate.ai': '#2e7d32',
  'assetsmanagement.ai': '#ed6c02',
  'infralytiqs.com': '#0288d1',
  'anamnesis.cloud': '#7b1fa2',
};

const formatCredit = (used: number | null | undefined, limit: number | null | undefined) => {
  if (used == null && limit == null) return '—';
  const u = used ?? 0;
  const lim = limit ?? 0;
  if (!lim || lim <= 0) return `${u.toLocaleString()} / Unlimited`;
  return `${u.toLocaleString()} / ${lim.toLocaleString()}`;
};

const MetricRow: React.FC<{ label: string; value: string; delayMs?: number }> = ({
  label,
  value,
  delayMs = 0,
}) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="baseline"
    gap={1}
    py={0.45}
    sx={{
      animation: `${fadeUp} 0.45s ease both`,
      animationDelay: `${delayMs}ms`,
      borderBottom: (t) => `1px solid ${
        t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
      }`,
      '&:last-of-type': { borderBottom: 'none' },
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
      {value}
    </Typography>
  </Box>
);

const renderDetails = (mod: ModuleOverviewItem) => {
  const d = mod.details || {};
  switch (mod.module) {
    case 'instantupdate.ai':
      return (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 700, display: 'block', mt: 1.25, mb: 0.5, letterSpacing: 0.3 }}
          >
            Monthly Credit Usage
          </Typography>
          <MetricRow label="WhatsApp" value={formatCredit(d.whatsAppUsed, d.whatsAppLimit)} delayMs={80} />
          <MetricRow label="Email" value={formatCredit(d.emailUsed, d.emailLimit)} delayMs={120} />
          <MetricRow label="SMS" value={formatCredit(d.smsUsed, d.smsLimit)} delayMs={160} />
          <MetricRow label="Telegram" value={formatCredit(d.telegramUsed, d.telegramLimit)} delayMs={200} />
        </>
      );
    case 'assetsmanagement.ai':
      return (
        <>
          <MetricRow
            label="Total Assets"
            value={d.totalAssets == null ? '—' : d.totalAssets.toLocaleString()}
            delayMs={80}
          />
          <MetricRow
            label="Published"
            value={d.publishedAssets == null ? '—' : d.publishedAssets.toLocaleString()}
            delayMs={120}
          />
        </>
      );
    case 'infralytiqs.com':
      return (
        <>
          <MetricRow
            label="ClickHouse Servers"
            value={d.clickHouseConfigs == null ? '—' : d.clickHouseConfigs.toLocaleString()}
            delayMs={80}
          />
          <MetricRow
            label="Loki Servers"
            value={d.lokiConfigs == null ? '—' : d.lokiConfigs.toLocaleString()}
            delayMs={120}
          />
          <MetricRow
            label="Report Suites"
            value={d.reportSuites == null ? '—' : d.reportSuites.toLocaleString()}
            delayMs={160}
          />
        </>
      );
    case 'anamnesis.cloud':
      return (
        <>
          <MetricRow
            label="Projects with Graph"
            value={d.projectsWithGraph == null ? '—' : d.projectsWithGraph.toLocaleString()}
            delayMs={80}
          />
          <MetricRow
            label="Total Prompts"
            value={d.totalPrompts == null ? '—' : d.totalPrompts.toLocaleString()}
            delayMs={120}
          />
        </>
      );
    default:
      return null;
  }
};

export const ModuleOverviewPanel: React.FC = () => {
  const theme = useTheme();
  const { hasModuleAccess, isUserSuperAdmin, user } = useModulePermissions();
  const company = useSelector((state: RootState) => state.authentication.company);
  const activeCompanyId = useSelector((state: RootState) => state.authentication.activeCompanyId);
  const companyId = activeCompanyId || (company as any)?._id;

  const [modules, setModules] = useState<ModuleOverviewItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);
  const [openDialog, setOpenDialog] = useState<DashboardDialog>(null);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res: any = await apiClient.get(`/company/${companyId}/module-overview`);
        const list: ModuleOverviewItem[] = Array.isArray(res?.data?.modules)
          ? res.data.modules
          : Array.isArray(res?.modules)
            ? res.modules
            : [];
        if (!cancelled) {
          setModules(list);
          setFetched(true);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to load module overview';
        Logger.error('ModuleOverviewPanel.tsx', msg);
        if (!cancelled) {
          setError(msg);
          setFetched(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const visibleModules = useMemo((): ModuleOverviewItem[] => {
    if (isUserSuperAdmin) return modules;

    const byModule = new Map(modules.map((mod) => [mod.module, mod]));
    const userModuleIds = (user?.moduleAndRole ?? [])
      .map((entry: { module: string; role: string }) => entry.module)
      .filter((moduleId: string) => MODULE_DISPLAY_NAMES[moduleId] && hasModuleAccess(moduleId));

    return userModuleIds.map(
      (moduleId: string) => byModule.get(moduleId) || emptyModuleOverview(moduleId),
    );
  }, [modules, hasModuleAccess, isUserSuperAdmin, user?.moduleAndRole]);

  if (!companyId) return null;
  if (fetched && !loading && !error && visibleModules.length === 0) return null;

  const dashPanel = theme.palette.dashboard;
  const cardBg = dashPanel?.statPanelBackgroundColor;
  const cardText = dashPanel?.statPanelTextColor;
  const iconColor =
    dashPanel?.statPanelPrimaryMetricColor
    ?? dashPanel?.heroTileForegroundColor
    ?? (theme.palette.mode === 'light' ? theme.palette.text.primary : theme.palette.secondary.main);
  const iconBg =
    dashPanel?.statPanelPrimaryIconBg
    ?? (theme.palette.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)');

  const handleCardClick = (mod: ModuleOverviewItem) => {
    if (mod.module === 'instantupdate.ai' || mod.module === 'infralytiqs.com') {
      setOpenDialog(mod.module);
    }
  };

  const isClickable = (moduleId: string) =>
    moduleId === 'instantupdate.ai' || moduleId === 'infralytiqs.com';

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
        Modules
      </Typography>

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={100}>
          <CircularProgress size={28} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && visibleModules.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gap: 2.5,
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              lg: 'repeat(4, 1fr)',
            },
            '& > *': { minWidth: 0 },
          }}
        >
          {visibleModules.map((mod: ModuleOverviewItem, index: number) => {
            const accent = MODULE_ACCENTS[mod.module] || theme.palette.primary.main;
            const clickable = isClickable(mod.module);
            return (
              <Card
                key={mod.module}
                elevation={0}
                onClick={() => handleCardClick(mod)}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onKeyDown={(e) => {
                  if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleCardClick(mod);
                  }
                }}
                sx={{
                  borderRadius: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: clickable ? 'pointer' : 'default',
                  bgcolor: cardBg
                    ?? (theme.palette.mode === 'dark' ? 'rgb(20, 25, 55)' : theme.palette.grey[50]),
                  color: cardText,
                  border: 'none',
                  '--glow-border': accent,
                  '--glow-soft': `${accent}55`,
                  '--glow-strong': `${accent}88`,
                  animation: `${fadeUp} 0.5s ease both, ${glowPulse} 3.2s ease-in-out infinite`,
                  animationDelay: `${index * 70}ms, ${index * 70}ms`,
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                  '&:hover': clickable
                    ? {
                        transform: 'translateY(-4px)',
                        '--glow-soft': `${accent}77`,
                        '--glow-strong': `${accent}aa`,
                      }
                    : {
                        transform: 'translateY(-2px)',
                      },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 'inherit',
                    pointerEvents: 'none',
                    background: `linear-gradient(135deg, ${accent}18 0%, transparent 42%)`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5, position: 'relative', '&:last-child': { pb: 2.5 } }}>
                  <Box display="flex" alignItems="center" gap={1.25} mb={1.75}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: iconBg,
                        color: iconColor,
                        flexShrink: 0,
                        '& .MuiSvgIcon-root': {
                          fontSize: 26,
                          color: 'inherit',
                        },
                      }}
                    >
                      {MODULE_ICONS[mod.module] || <PeopleIcon />}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={800} noWrap>
                        {mod.displayName}
                      </Typography>
                    </Box>
                  </Box>

                  <MetricRow
                    label="Users"
                    value={`${mod.registeredUsers.toLocaleString()} / ${mod.maxUsers.toLocaleString()}`}
                    delayMs={40}
                  />
                  {renderDetails(mod)}

                  {clickable && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 1.5,
                        fontWeight: 700,
                        color: accent,
                        animation: `${fadeUp} 0.45s ease both`,
                        animationDelay: '240ms',
                      }}
                    >
                      Click to open dashboard →
                    </Typography>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      <Dialog
        open={openDialog === 'instantupdate.ai'}
        onClose={() => setOpenDialog(null)}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '92vh',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          Instant Update Dashboard
          <IconButton aria-label="Close" onClick={() => setOpenDialog(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          {openDialog === 'instantupdate.ai' && <InstantUpdateDashboard embedded />}
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDialog === 'infralytiqs.com'}
        onClose={() => setOpenDialog(null)}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '92vh',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          Infralytiqs Dashboard
          <IconButton aria-label="Close" onClick={() => setOpenDialog(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          {openDialog === 'infralytiqs.com' && <InfralytiqsDashboard embedded />}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
